import { isArray, isObject, isString, transform } from 'lodash';

/**
 * Sanitizes objects by normalizing types and structure
 *
 * This utility performs several normalizations:
 * - Converts string numbers to actual numbers (e.g., "123" -> 123)
 * - Removes properties with undefined values to ensure consistent object structure
 *
 * This is useful for normalizing data from different sources (forms, APIs, storage)
 * to ensure consistent types and structure.
 *
 * @param obj - The object to sanitize
 * @returns A new object with normalized values and consistent structure
 *
 * @example
 * sanitizeObject({
 *   x: "123",
 *   y: "45.67",
 *   z: "not a number",
 *   undefinedProp: undefined
 * })
 * // Returns: { x: 123, y: 45.67, z: "not a number" }
 */
export const sanitizeObject = (obj: any): any => {
  const sanitizeValue = (value: any): any => {
    if (isString(value) && /^-?\d+(\.\d+)?$/.test(value.trim())) {
      const num = value.includes('.') ? parseFloat(value) : parseInt(value, 10);
      return isNaN(num) ? value : num;
    }
    return value;
  };

  // Handle primitive values directly
  if (!isObject(obj) || obj === null) {
    return sanitizeValue(obj);
  }

  // Handle arrays
  if (isArray(obj)) {
    return obj.map(sanitizeObject);
  }

  // Handle objects
  return transform(obj, (result: any, value: any, key: any) => {
    // Skip undefined values to ensure consistent object structure
    if (value === undefined) {
      return;
    }

    result[key] = sanitizeObject(value);
  });
};
